import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { teamAbbreviation, usesTeamLogos } from '../../domain/countdown-bar.helpers';
import type { CountdownBarItem } from '../../store/countdown-bar.store';
import { GamesTypes } from '@app/domain/enums/game';
import type { GameTeam } from '@app/domain/interfaces/game/game-team.interface';

type TeamSide = 'left' | 'right';

const GROUP_SIZE = 3;
const GROUP_END = 6;
const LEFT_TEAM_INDEX = 0;
const RIGHT_TEAM_INDEX = 2;

@Component({
  selector: 'bingo-countdown-bar-team-side',
  templateUrl: './countdown-bar-team-side.html',
  styleUrl: './countdown-bar-team-side.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    'class': 'countdown-bar__teams',
    '[class.countdown-bar__teams--left]': 'side() === \'left\'',
    '[class.countdown-bar__teams--right]': 'side() === \'right\'',
  },
})
export class CountdownBarTeamSideComponent {
  protected readonly teamAbbreviation = teamAbbreviation;
  protected readonly stackedUrls = computed<string[]>(() => this.resolveStackedUrls());
  protected readonly singleTeam = computed<GameTeam | null>(() => this.resolveSingleTeam());
  readonly item = input.required<CountdownBarItem>();
  readonly side = input.required<TeamSide>();

  private resolveStackedUrls(): string[] {
    const item = this.item();
    const isLeft = this.side() === 'left';

    if (usesTeamLogos(item.type)) {
      const logos = (item.game.team_logos ?? []).filter(Boolean);

      return isLeft ? logos.slice(0, GROUP_SIZE) : logos.slice(GROUP_SIZE, GROUP_END);
    }

    return this.resolveGroupedUrls(item, isLeft);
  }

  private resolveGroupedUrls(item: CountdownBarItem, isLeft: boolean): string[] {
    const teams = item.game.game_teams;

    if (item.type !== GamesTypes.GOAL_RUSH || teams.length < GROUP_END) {
      return [];
    }

    const group = isLeft ? teams.slice(0, GROUP_SIZE) : teams.slice(GROUP_SIZE, GROUP_END);

    return group.map(team => team.URI);
  }

  private resolveSingleTeam(): GameTeam | null {
    const item = this.item();
    const teamIndex = this.side() === 'left' ? LEFT_TEAM_INDEX : RIGHT_TEAM_INDEX;

    return item.game.game_teams.find(team => team.teamIndex === teamIndex) ?? null;
  }
}
